import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import IconButton from 'LuxKit/Button/IconButton'
import LinePlayIcon from 'LuxKit/Icons/line/LinePlayIcon'
import LinePauseIcon from 'LuxKit/Icons/line/LinePauseIcon'
import LineVolumeUpIcon from 'LuxKit/Icons/line/LineVolumeUpIcon'
import LineVolumeMuteIcon from 'LuxKit/Icons/line/LineVolumeMuteIcon'
import LineShareIcon from 'LuxKit/Icons/line/LineShareIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { isHeadlessModeSel } from 'selectors/configSelectors'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions } from 'contexts/Wrapped'
import useWrappedShare from '../../hooks/useWrappedShare'

interface Props {
  onClose: () => void
}

function WrappedHeaderActions({ onClose }: Props) {
  const intl = useIntl()
  const dispatch = useWrappedStoryDispatch()
  const { handleShare } = useWrappedShare()
  const isHeadless = useAppSelector(isHeadlessModeSel)

  // Use granular selectors to minimize re-renders
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isMuted = useWrappedStorySelector(state => state.isMuted)
  const data = useWrappedStorySelector(state => state.data)

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  const pauseAriaLabel = intl.formatMessage({ id: 'k65uSD9GhI', defaultMessage: 'Pause', description: 'Accessible aria-label for the pause button on the video or media player control' })
  const playAriaLabel = intl.formatMessage({ id: 'ClYwIzsSFU', defaultMessage: 'Play', description: 'Accessible aria-label for the play button on the video or media player control' })
  const muteAriaLabel = intl.formatMessage({ id: 'ZYrD4UsPP4', defaultMessage: 'Mute', description: 'Accessible aria-label for the mute button on the video or media player control' })
  const unmuteAriaLabel = intl.formatMessage({ id: 'BKsr1UL1Qr', defaultMessage: 'Unmute', description: 'Accessible aria-label for the unmute button on the video or media player control' })

  const handleTogglePause = useCallback(() => {
    dispatch({ type: WrappedStoryActions.togglePause, isPaused: undefined })
  }, [dispatch])

  const handleToggleMute = useCallback(() => {
    dispatch({ type: WrappedStoryActions.toggleMute, isMuted: undefined })
  }, [dispatch])

  return (
    <Group direction="horizontal" gap={4} verticalAlign="center">
      <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleTogglePause}
        aria-label={isPaused ? playAriaLabel : pauseAriaLabel}
      >
        {isPaused ? <LinePlayIcon /> : <LinePauseIcon />}
      </IconButton>
      <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleToggleMute}
        aria-label={isMuted ? unmuteAriaLabel : muteAriaLabel}
      >
        {isMuted ? <LineVolumeMuteIcon /> : <LineVolumeUpIcon />}
      </IconButton>
      {!isTotalsOnlyMode && <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleShare}
        aria-label={intl.formatMessage({ id: 'BcOE9GIaeo', defaultMessage: 'Share', description: 'Accessible aria-label for the share button to share the current offer or content' })}
      >
        <LineShareIcon />
      </IconButton>}
      {!isHeadless && (
        <IconButton
          kind="tertiary"
          variant="ghost"
          size="small"
          onClick={onClose}
          aria-label={intl.formatMessage({ id: 'MSOK5taw+l', defaultMessage: 'Close', description: 'Accessible aria-label for the close button to dismiss the current modal or panel' })}
        >
          <LineTimesIcon />
        </IconButton>
      )}
    </Group>
  )
}

export default React.memo(WrappedHeaderActions)
